// src/components/Customer/Buildings/CustomerBuildingsTable.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import BuildingTableHeader from "./BuildingTableHeader";
import BuildingRow from "./Rows/BuildingRow";
import {
  EDIT_COLUMNS,
  VIEW_COLUMNS,
} from "../../../config/buildingColumnsConfig";
import { useCustomerBuildings } from "../../../hooks/useCustomerBuildings";
import {
  setSelectedCustomerBuilding,
  clearSelectedCustomerBuilding,
  selectSelectedCustomerBuilding,
} from "../../../../../../../redux/customers/selectedCustomerBuildingSlice";
import useDebounce from "../../../../../../../hooks/useDebounce";
import useAutoSaveBuildings from "../../../hooks/useAutoSaveBuildings";
// import useAutoSaveBuilding from './hooks/useAutoSaveBuilding';

const CustomerBuildingsTable = ({
  customerID,
  isEditMode,
  newBuildingId,
  setNewBuildingId,
  onAddNew,
}) => {
  const dispatch = useDispatch();
  const selectedBuilding = useSelector(selectSelectedCustomerBuilding);

  const {
    assignedBuildings,
    buildingOptions,
    loadAssignedBuildings,
    loadBuildingOptions,
    addBuildingToCustomer,
    removeBuildingFromCustomer,
    updateBuildingData,
  } = useCustomerBuildings(customerID);

  const [showAllBuildings, setShowAllBuildings] = useState(false);

  // Load assigned buildings when the customerID changes.
  useEffect(() => {
    if (customerID) {
      loadAssignedBuildings();
    }
  }, [customerID, loadAssignedBuildings]);

  // Load building options on mount.
  useEffect(() => {
    loadBuildingOptions();
  }, [loadBuildingOptions]);

  // const autoSaveBuilding = useAutoSaveBuilding(assignedBuildings, updateBuildingData);
  const debouncedBuildings = useDebounce(assignedBuildings, 500);
  useAutoSaveBuildings(
    assignedBuildings,
    debouncedBuildings,
    updateBuildingData,
    isEditMode
  );

  // Handler for field changes in a building row.
  const handleBuildingFieldChange = useCallback(
    (buildingID, key, value) => {
      const building = assignedBuildings.find(
        (b) => b.buildingID === buildingID
      );
      if (building) {
        const updatedBuilding = { ...building, [key]: value };
        updateBuildingData(buildingID, updatedBuilding);
      }
    },
    [assignedBuildings, updateBuildingData]
  );

  // Compute the displayed buildings.
  const displayedBuildings = useMemo(() => {
    if (!assignedBuildings || assignedBuildings.length === 0) return [];
    return showAllBuildings
      ? assignedBuildings
      : assignedBuildings.slice(0, 10);
  }, [assignedBuildings, showAllBuildings]);

  // Toggle row selection.
  const handleRowClick = useCallback(
    (building) => {
      if (
        selectedBuilding &&
        selectedBuilding.buildingID === building.buildingID
      ) {
        dispatch(clearSelectedCustomerBuilding());
      } else {
        dispatch(setSelectedCustomerBuilding(building));
      }
    },
    [selectedBuilding, dispatch]
  );

  if (!assignedBuildings) {
    return <div>Loading buildings...</div>;
  }

  const editHandlers = {
    handleBuildingFieldChange,
    removeBuilding: removeBuildingFromCustomer,
  };

  return (
    <div className="mb-4">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="sticky top-0 z-10">
            <BuildingTableHeader isEditMode={isEditMode} onAddNew={onAddNew} />
          </thead>
          <tbody>
            {isEditMode && (
              <BuildingRow
                mode="add"
                buildingOptions={buildingOptions}
                addBuilding={addBuildingToCustomer}
                newBuildingId={newBuildingId}
                setNewBuildingId={setNewBuildingId}
              />
            )}
            {isEditMode
              ? displayedBuildings.map((building, index) => (
                  <BuildingRow
                    key={building.buildingID}
                    mode="edit"
                    building={building}
                    index={index}
                    selectedBuilding={selectedBuilding}
                    onRowClick={handleRowClick}
                    handlers={editHandlers}
                    // handleAutoSave={() => autoSaveBuilding(index)}
                  />
                ))
              : displayedBuildings.map((building) => (
                  <BuildingRow
                    key={building.buildingID}
                    mode="view"
                    building={building}
                    selectedBuilding={selectedBuilding}
                    onRowClick={handleRowClick}
                  />
                ))}
            {assignedBuildings.length > 10 && !showAllBuildings && (
              <tr>
                <td
                  colSpan={
                    isEditMode ? EDIT_COLUMNS.length + 1 : VIEW_COLUMNS.length
                  }
                  className="text-center py-1"
                >
                  <button
                    onClick={() => setShowAllBuildings(true)}
                    className="px-1 py-1 border bg-sss-blue text-white font-semibold hover:bg-sss-blue-sel transition duration-150"
                  >
                    Show more
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

CustomerBuildingsTable.propTypes = {
  customerID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  isEditMode: PropTypes.bool.isRequired,
  newBuildingId: PropTypes.string,
  setNewBuildingId: PropTypes.func.isRequired,
  onAddNew: PropTypes.func.isRequired,
};

export default CustomerBuildingsTable;
